import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import type { Delta } from './time';
import { TONE_COLORS } from './status';

export interface StatTileProps {
  label: ReactNode;
  /** Plain-text name, used for the accessible name when `label` is not a string. */
  ariaLabel?: string;
  value: string;
  hint?: ReactNode;
  /** Change versus a baseline; null shows why there is none: `deltaLabel` when it is a reason, else « pas de données sur la période précédente ». */
  delta?: Delta | null;
  /** Text after the delta, e.g. « vs 90 j précédents ». */
  deltaLabel?: ReactNode;
  /** Explanation shown on hover over the label. */
  help?: string;
  /** Larger figure for the main indicator of a page. */
  hero?: boolean;
  /** Makes the whole tile a link. */
  to?: string;
  loading?: boolean;
  error?: Error | boolean;
  /** Color of the value (status colors). */
  color?: string;
  /** Draw a border around the tile (default when it is a link). */
  outlined?: boolean;
}

const deltaColor = (good: boolean | null) =>
  good === null
    ? TONE_COLORS.neutral
    : good
    ? TONE_COLORS.success
    : TONE_COLORS.error;

/** One KPI: label, big figure, optional delta and hint. Used by every dashboard. */
export function StatTile({
  label,
  ariaLabel,
  value,
  hint,
  delta,
  deltaLabel,
  help,
  hero,
  to,
  loading,
  error,
  color,
  outlined,
}: StatTileProps) {
  const shown = error ? '—' : loading ? '…' : value;
  const body = (
    <div style={{ padding: outlined || to ? '16px 20px' : 0, height: '100%' }}>
      <Tooltip title={help ?? ''} disableHoverListener={!help} arrow>
        <Typography
          variant="body2"
          color="textSecondary"
          component="p"
          style={{ fontWeight: 500 }}
        >
          {label}
        </Typography>
      </Tooltip>
      <Typography
        variant={hero ? 'h3' : 'h5'}
        component="p"
        style={{
          margin: '6px 0 2px',
          color,
          fontSize: hero ? 36 : 28,
          fontWeight: 600,
          lineHeight: 1.2,
          letterSpacing: '-0.02em',
        }}
      >
        {shown}
      </Typography>
      {error ? (
        <Typography variant="caption" color="textSecondary">
          Indisponible
        </Typography>
      ) : (
        <>
          {delta !== undefined && (
            <Typography
              variant="body2"
              style={{ color: deltaColor(delta?.good ?? null) }}
            >
              {delta ? (
                <>
                  {delta.text}
                  {deltaLabel && (
                    <span style={{ color: TONE_COLORS.neutral }}>
                      {' '}
                      {deltaLabel}
                    </span>
                  )}
                </>
              ) : (
                // No comparison: the reason when the caller gives one (« suivi prod trop
                // récent pour comparer »), not a « vs … » label that compares nothing.
                <span style={{ color: TONE_COLORS.neutral }}>
                  {typeof deltaLabel === 'string' &&
                  !deltaLabel.startsWith('vs')
                    ? deltaLabel
                    : 'pas de données sur la période précédente'}
                </span>
              )}
            </Typography>
          )}
          {hint && (
            <Typography variant="caption" color="textSecondary">
              {hint}
            </Typography>
          )}
        </>
      )}
    </div>
  );

  if (to) {
    return (
      <RouterLink
        to={to}
        aria-label={`${
          ariaLabel ?? (typeof label === 'string' ? label : '')
        } : ${shown}`}
        style={{
          display: 'block',
          height: '100%',
          color: 'inherit',
          textDecoration: 'none',
        }}
      >
        <Paper variant="outlined" style={{ height: '100%' }}>
          {body}
        </Paper>
      </RouterLink>
    );
  }
  return outlined ? (
    <Paper variant="outlined" style={{ height: '100%' }}>
      {body}
    </Paper>
  ) : (
    body
  );
}

/** Responsive row of tiles: pass `columns` tiles per row on wide screens (default: as many as children). */
export function StatTileGrid({
  children,
  spacing = 2,
  columns,
}: {
  children: ReactNode[] | ReactNode;
  spacing?: 1 | 2 | 3 | 4;
  /** Tiles per row on wide screens (1, 2, 3, 4, 6 or 12); default: one row. */
  columns?: 1 | 2 | 3 | 4 | 6 | 12;
}) {
  const items = Array.isArray(children) ? children.filter(Boolean) : [children];
  const md = (
    columns
      ? Math.floor(12 / columns)
      : Math.max(1, Math.floor(12 / Math.max(items.length, 1)))
  ) as 1 | 2 | 3 | 4 | 6 | 12;
  return (
    <Grid container spacing={spacing}>
      {items.map((child, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Grid item xs={12} sm={6} md={md} key={index}>
          {child}
        </Grid>
      ))}
    </Grid>
  );
}
